import React, { useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import { useSnackbar } from 'material-ui-snackbar-provider';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import { isNumber } from 'lodash';
import cx from 'classnames';

import { FOLDER_CONTENT_FILTER } from '../../enums/ENUMS';
import { updateComposerPixelPosition, updateComposerPlayControls, updateProgressPosition } from '../../store/composers/composersSlice';
import { saveCurrentPlayingTimelineClip, updateNotes, updateQualifiers } from '../../store/videos/videoSlice';
import { selectShareFolder, switchShareModal } from '../../store/folders/foldersSlice';
import { updateSortOption, updateSortType } from '../../store/videoFeedsList/videoFeedsListSlice';
import VideofeedShareModal from '../SharingModals/MyFolderPage/VideofeedShareModal';
import ShareFolderModal from '../SharingModals/MyFolderPage/ShareFolderModal';
import { switchSidebar } from '../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { PaginationFooter } from '../modules/PaginationFooter';
import { FOLDER_TYPE, VIDEOFEED } from '../../enums/TYPES';
import { SortOptions, SortType } from '../../enums/SORT';
import { getFolderContents } from '../../api/videofeeds';
import LinearProgress from '../modules/LinearProgress';
import { OrderSelector } from '../modules/Selector';
import styles from './VideofeedsList.module.scss';
import SearchInput from '../modules/SearchInput';
// import { getShallowFolder } from '../../api';
import {
  getMyVideofeedsPagePaginationLimit,
  getParentFoldersSortOptionAndType,
  getSortStateForParentFolders,
  setMyVideofeedsPagePaginationLimit,
  setParentFoldersSortOptionAndType,
  useDimensions,
} from '../../helpers';
import CollaborativeVideofeed from './CollaborativeVideofeed';
import { PATHS } from '../../enums';
import { getCollaborativeFolderById } from '../../api/collaborativeFoldersApi';
import FolderContentRow from '../modules/Folders/FolderContentRow';
import { ContainedButton } from '../modules/Buttons';
import DateSortHeader from '../modules/DateSortHeader';

type FOLDER_CONTENT_ITEM = (FOLDER_TYPE & { item_type: 'folder' }) | (VIDEOFEED & { item_type?: 'videofeed' });

const CONTENT_FILTER_OPTIONS = [
  { label: 'All Items', value: FOLDER_CONTENT_FILTER.ALL, order: SortType.ASC },
  { label: 'Timelines', value: FOLDER_CONTENT_FILTER.TIMELINE, order: SortType.ASC },
  { label: 'Composers', value: FOLDER_CONTENT_FILTER.COMPOSER, order: SortType.ASC },
  { label: 'Subfolders', value: FOLDER_CONTENT_FILTER.SUBFOLDER, order: SortType.ASC },
];

const getContentFilter = (value?: string | null): FOLDER_CONTENT_FILTER => {
  switch (value) {
    case FOLDER_CONTENT_FILTER.TIMELINE:
    case FOLDER_CONTENT_FILTER.COMPOSER:
    case FOLDER_CONTENT_FILTER.SUBFOLDER:
      return value;
    default:
      return FOLDER_CONTENT_FILTER.ALL;
  }
};

const CollaborativeVideofeedsList = () => {
  const [folderContents, setFolderContents] = useState<FOLDER_CONTENT_ITEM[]>([]);
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const { refetchFeedList, sortOption, sort } = useAppSelector((state) => state.videoFeedsListStore);
  const { triggerFolderFetch } = useAppSelector((state) => state.folderStore);
  const { composerPlayControl } = useAppSelector((state) => state.composersStore);
  const { showSidebar } = useAppSelector((state) => state.sharedStore);
  const { myOrganization } = useAppSelector((state) => state.user);

  const { widthD } = useDimensions();
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const folderId = searchParams.get('folderId');
  const folderTrail = searchParams.get('folderTrail');
  const [contentFilter, setContentFilter] = useState<FOLDER_CONTENT_FILTER>(() => getContentFilter(searchParams.get('contentFilter')));

  const [folder, setFolder] = useState<FOLDER_TYPE>();
  useEffect(() => {
    if (folderId) {
      fetchFolder(folderId);
    }
  }, [folderId]);

  const fetchFolder = async (id: string) => {
    try {
      const res = await getCollaborativeFolderById(+id);
      setFolder(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [fetchTrigger, setFetchTrigger] = useState({});

  useEffect(() => {
    setContentFilter(getContentFilter(searchParams.get('contentFilter')));
  }, [location.search]);

  useEffect(() => {
    dispatch(updateNotes([]));
    dispatch(saveCurrentPlayingTimelineClip(null));
    dispatch(updateQualifiers([]));
    const prefferedLimit = getMyVideofeedsPagePaginationLimit();
    if (prefferedLimit && isNumber(+prefferedLimit)) {
      setRowsPerPage(+prefferedLimit);
      onPaginate(null, 0);
    }
    dispatch(switchSidebar(false));
    dispatch(updateProgressPosition(0));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: 0,
      }),
    );
    dispatch(updateComposerPixelPosition(0));
  }, []);

  const fetchVideofeeds = async (arg?: { sortFromArg?: SortType; sortOpinionFromArg?: SortOptions; signal?: AbortSignal }) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setLoading(true);

    const prefferedLimit = getMyVideofeedsPagePaginationLimit();

    try {
      const res = await getFolderContents({
        folderId: folderId as string,
        page,
        limit: prefferedLimit && isNumber(+prefferedLimit) ? +prefferedLimit : rowsPerPage,
        search,
        sort: arg?.sortFromArg ? arg.sortFromArg : sort,
        sortOption: arg?.sortOpinionFromArg ? arg.sortOpinionFromArg : sortOption,
        view: 'collaborative',
        contentFilter,
        signal: arg?.signal,
      });
      setFolderContents(res.documents);
      setTotal(res.total);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return;
      }
      if (err?.response?.data?.message) {
        history.push(PATHS.MY_FOLDERS.path);
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    const res = getParentFoldersSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        dispatch(updateSortType(sortAndOption[0]));
        dispatch(updateSortOption(sortAndOption[1]));
        fetchVideofeeds({
          sortFromArg: sortAndOption[0] as SortType,
          sortOpinionFromArg: sortAndOption[1] as SortOptions,
          signal: abortController.signal,
        });
      }
    } else if (folderId) {
      fetchVideofeeds({ signal: abortController.signal });
    }
    return () => {
      abortController.abort();
    };
  }, [debouncedValue, page, rowsPerPage, sort, refetchFeedList, sortOption, fetchTrigger, folderId, triggerFolderFetch, contentFilter]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    onPaginate(null, 0);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setMyVideofeedsPagePaginationLimit(parseInt(e.target.value, 10));
    onPaginate(null, 0);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const sortHandler = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    const selectedSortOption = option ? (option.value as SortOptions) : singleValue;
    if (option) {
      dispatch(updateSortType(option.order));
      setParentFoldersSortOptionAndType(option.order, option.value as SortOptions);
    } else if (sortOption === singleValue) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      dispatch(updateSortType(sortT));
      setParentFoldersSortOptionAndType(sortT, singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForParentFolders()!);
      let sortType = SortType.ASC;
      if (sortState && sortState[singleValue]) {
        sortType = sortState[singleValue];
      }
      dispatch(updateSortType(sortType));
      setParentFoldersSortOptionAndType(sortType, singleValue);
    }
    dispatch(updateSortOption(selectedSortOption));
    onPaginate(null, 0);
  };

  const onContentFilterSelect = (_: any, option?: { label: string; value: string; order: SortType }) => {
    if (!option) return;
    const nextFilter = option.value as FOLDER_CONTENT_FILTER;
    const nextSearch = new URLSearchParams(location.search);

    if (nextFilter === FOLDER_CONTENT_FILTER.ALL) {
      nextSearch.delete('contentFilter');
    } else {
      nextSearch.set('contentFilter', nextFilter);
    }

    history.replace(`${location.pathname}${nextSearch.toString() ? `?${nextSearch.toString()}` : ''}`);
    setContentFilter(nextFilter);
    onPaginate(null, 0);
  };

  const redirectToFoldes = () => {
    history.push(PATHS.COLLABORATIVE_FOLDERS.path);
  };

  const breadcrumbAncestorIds = useMemo(() => (folderTrail ? folderTrail.split(',').filter(Boolean) : []), [folderTrail]);

  const onHierarchyBackClick = () => {
    const parentFolderId = breadcrumbAncestorIds[breadcrumbAncestorIds.length - 1];
    if (!parentFolderId) {
      return;
    }

    setPage(0);

    const nextTrail = breadcrumbAncestorIds.length > 1 ? breadcrumbAncestorIds.slice(0, breadcrumbAncestorIds.length - 1).join(',') : '';

    history.push(
      `${PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path}?folderId=${parentFolderId}&parentPage=${PATHS.COLLABORATIVE_FOLDERS.label}${
        nextTrail ? `&folderTrail=${encodeURIComponent(nextTrail)}` : ''
      }${contentFilter !== FOLDER_CONTENT_FILTER.ALL ? `&contentFilter=${contentFilter}` : ''}`,
    );
  };

  const customWidth = useMemo(() => {
    if (widthD < 500) {
      return `calc(100vw - 80px)`;
    }
    return `calc(100vw - ${showSidebar ? 315 : 64}px - 64px)`;
  }, [showSidebar, widthD]);

  const listRef = useRef<HTMLDivElement>(null);

  // const videofeedsList = useMemo(
  //   () =>
  //     folderContents.filter(
  //       (item) => item.item_type !== 'folder',
  //     ) as VIDEOFEED[],
  //   [folderContents],
  // );

  const nextFolderTrail = useMemo(() => {
    const trailParts = folderTrail ? folderTrail.split(',').filter(Boolean) : [];
    if (folderId && trailParts[trailParts.length - 1] !== folderId) {
      trailParts.push(folderId);
    }

    return trailParts.join(',');
  }, [folderId, folderTrail]);

  return (
    <div className={styles.myFolders}>
      <div className={styles.topBar}>
        <div className={styles.smallPath} onClick={redirectToFoldes}>
          Parent Folders / {folder?.name}
        </div>
        <div className={styles.pageTitle}>
          {!!breadcrumbAncestorIds.length && (
            <ContainedButton kind="purple" className={styles.backButton} onClick={onHierarchyBackClick}>
              Back
            </ContainedButton>
          )}
          <span>{folder?.name}</span>
        </div>
      </div>

      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={onSearch} className={styles.searchBar} />
        <div className={styles.sortToolbar}>
          <OrderSelector
            title={'Sorting by'}
            selectedValue={sortOption}
            selectedOrder={sort}
            options={[
              { label: 'A-Z ↓ ', value: SortOptions.NAME, order: SortType.ASC },
              { label: 'Z-A ↑ ', value: SortOptions.NAME, order: SortType.DESC },
              { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.DESC },
              { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.ASC },
              {
                label: 'Last Viewed ↓',
                value: SortOptions.LAST_VIEWED,
                order: SortType.DESC,
              },
              {
                label: 'Last Viewed ↑',
                value: SortOptions.LAST_VIEWED,
                order: SortType.ASC,
              },
              {
                label: 'Last Modified ↓',
                value: SortOptions.UPDATED_AT,
                order: SortType.DESC,
              },
              {
                label: 'Last Modified ↑',
                value: SortOptions.UPDATED_AT,
                order: SortType.ASC,
              },
              {
                label: 'Created At ↓',
                value: SortOptions.CREATED_AT,
                order: SortType.DESC,
              },
              {
                label: 'Created At ↑',
                value: SortOptions.CREATED_AT,
                order: SortType.ASC,
              },
            ]}
            onSelect={sortHandler}
          />
        </div>

        <div className={styles.filterToolbar}>
          <OrderSelector
            title={'Filter by'}
            selectedValue={contentFilter}
            selectedOrder={SortType.ASC}
            options={CONTENT_FILTER_OPTIONS}
            onSelect={onContentFilterSelect}
          />
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.container}>
          <div className={styles.leftSide}>
            <div className={styles.name}>
              <button type="button" onClick={() => (loading ? '' : sortHandler(SortOptions.NAME))} className={styles.btn}>
                Name
                {sortOption === SortOptions.NAME && sort === SortType.ASC && <KeyboardArrowDownIcon className={styles.arrow} />}
                {sortOption === SortOptions.NAME && sort === SortType.DESC && <KeyboardArrowUpIcon className={styles.arrow} />}
              </button>
            </div>
          </div>
          <div className={styles.rightSide}>
            <div className={styles.team}>
              Status
              <br />
              Collaborators
            </div>
            <div className={styles.date}>
              <DateSortHeader
                sortOption={sortOption}
                sort={sort}
                compact={widthD <= 1330}
                disabled={loading}
                onSelect={sortHandler}
                arrowClassName={styles.arrow}
              />
            </div>
            <div className={styles.size} onClick={() => sortHandler(SortOptions.SIZE)}>
              {widthD > 1330 ? 'File Size' : 'Size'}
              {sortOption === SortOptions.SIZE && sort === SortType.DESC && <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />}
              {sortOption === SortOptions.SIZE && sort === SortType.ASC && <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />}
            </div>
            <div className={styles.duration}>{widthD > 1430 ? 'Upload Hours' : 'Hours'}</div>
            <div className={styles.actionBtns}>Actions</div>
          </div>
        </div>
      </div>

      <div ref={listRef} className={cx(styles.list)} id={'videofeed_custom_scroller'}>
        {loading && <LinearProgress />}
        {folderContents.length > 0 &&
          folderContents.map((item, i) => (
            <div key={`${item.item_type || 'videofeed'}-${item.id}`} className={'mb-5'} style={{ width: customWidth }}>
              {item.item_type === 'folder' ? (
                <FolderContentRow
                  folder={item}
                  variant="collaborative"
                  dateSortOption={sortOption}
                  onOpen={() => {
                    if (String(item.id) === String(folderId)) {
                      return;
                    }

                    history.push(
                      `${PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path}?folderId=${item.id}&parentPage=${PATHS.COLLABORATIVE_FOLDERS.label}${
                        nextFolderTrail ? `&folderTrail=${encodeURIComponent(nextFolderTrail)}` : ''
                      }${contentFilter !== FOLDER_CONTENT_FILTER.ALL ? `&contentFilter=${contentFilter}` : ''}`,
                    );
                  }}
                  onShare={() => {
                    dispatch(selectShareFolder(item));
                    dispatch(switchShareModal(true));
                  }}
                />
              ) : (
                <CollaborativeVideofeed
                  loading={loading}
                  feedIndex={i}
                  videofeed={item}
                  fetchAll={fetchVideofeeds}
                  orgType={myOrganization?.type}
                  dateSortOption={sortOption}
                />
              )}
            </div>
          ))}
      </div>

      <PaginationFooter
        hideVersion
        page={page}
        total={total}
        onPageChange={onPaginate}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsChange}
      />

      <VideofeedShareModal onUpdate={setFetchTrigger} />
      <ShareFolderModal setFetchTrigger={(n) => setFetchTrigger({ at: n })} />
    </div>
  );
};

export default CollaborativeVideofeedsList;
